import { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/TradingService';
import type { MarketAssetClass, PrevCloseValue } from '../../types/trading';

type CacheKey = `${MarketAssetClass}:${string}`;
type PrevCloseBySymbol = Record<string, PrevCloseValue | undefined>;

const prevCloseCache = new Map<CacheKey, PrevCloseValue>();
const inFlightByKey = new Map<CacheKey, Promise<void>>();
const OPTION_SYMBOL_PATTERN = /^(?:O:)?[A-Z]+\d{6}[CP]\d+$/;

const normalizeSymbol = (symbol: string): string => symbol.trim().toUpperCase();

const getAssetClassForSymbol = (symbol: string): MarketAssetClass => {
  const normalized = normalizeSymbol(symbol);
  if (normalized.includes('/')) return 'crypto';
  if (OPTION_SYMBOL_PATTERN.test(normalized)) return 'option';
  return 'stock';
};

const toCacheKey = (assetClass: MarketAssetClass, symbol: string): CacheKey => {
  return `${assetClass}:${symbol}`;
};

const unavailableValue = (reason: string): PrevCloseValue => ({
  prev_close: null,
  prev_close_date: null,
  status: 'unavailable',
  reason,
});

export const __resetStockMonitorPrevCloseCacheForTests = () => {
  prevCloseCache.clear();
  inFlightByKey.clear();
};

const readFromCache = (symbols: string[]): PrevCloseBySymbol => {
  const values: PrevCloseBySymbol = {};
  symbols.forEach((symbol) => {
    const assetClass = getAssetClassForSymbol(symbol);
    const cacheKey = toCacheKey(assetClass, symbol);
    values[symbol] = prevCloseCache.get(cacheKey);
  });
  return values;
};

export const useStockMonitorPrevClose = (symbols: string[]): PrevCloseBySymbol => {
  const normalizedSymbols = useMemo(
    () => Array.from(new Set(symbols.map(normalizeSymbol).filter((symbol) => symbol.length > 0))),
    [symbols],
  );
  const symbolsKey = normalizedSymbols.join('|');
  const [valuesBySymbol, setValuesBySymbol] = useState<PrevCloseBySymbol>(() =>
    readFromCache(normalizedSymbols),
  );

  useEffect(() => {
    let canceled = false;

    const refreshFromCache = () => {
      if (canceled) return;
      setValuesBySymbol(readFromCache(normalizedSymbols));
    };

    refreshFromCache();

    const symbolsToFetchByAsset: Record<MarketAssetClass, string[]> = {
      stock: [],
      crypto: [],
      option: [],
    };
    const pendingRequests: Promise<void>[] = [];

    normalizedSymbols.forEach((symbol) => {
      const assetClass = getAssetClassForSymbol(symbol);
      const key = toCacheKey(assetClass, symbol);
      if (prevCloseCache.has(key)) {
        return;
      }

      const existingRequest = inFlightByKey.get(key);
      if (existingRequest) {
        pendingRequests.push(existingRequest);
        return;
      }

      symbolsToFetchByAsset[assetClass].push(symbol);
    });

    (['stock', 'crypto', 'option'] as MarketAssetClass[]).forEach((assetClass) => {
      const symbolsForAsset = symbolsToFetchByAsset[assetClass];
      if (symbolsForAsset.length === 0) return;

      const request = tradingService
        .getPrevClose(assetClass, symbolsForAsset)
        .then((response) => {
          symbolsForAsset.forEach((symbol) => {
            const key = toCacheKey(assetClass, symbol);
            prevCloseCache.set(
              key,
              response.values[symbol] ?? unavailableValue('previous daily close unavailable'),
            );
          });
        })
        .catch(() => {
          symbolsForAsset.forEach((symbol) => {
            const key = toCacheKey(assetClass, symbol);
            prevCloseCache.set(
              key,
              unavailableValue('failed to fetch previous daily close'),
            );
          });
        })
        .finally(() => {
          symbolsForAsset.forEach((symbol) => {
            const key = toCacheKey(assetClass, symbol);
            inFlightByKey.delete(key);
          });
        });

      symbolsForAsset.forEach((symbol) => {
        const key = toCacheKey(assetClass, symbol);
        inFlightByKey.set(key, request);
      });
      pendingRequests.push(request);
    });

    if (pendingRequests.length > 0) {
      void Promise.allSettled(pendingRequests).then(() => {
        refreshFromCache();
      });
    }

    return () => {
      canceled = true;
    };
  }, [symbolsKey, normalizedSymbols]);

  return valuesBySymbol;
};
